<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import OnboardingStepLayout from './OnboardingStepLayout.vue';
import { useRegisterOnboardingNav } from '../useOnboardingNav';

const emit = defineEmits(['back', 'select']);

const store = useStore();
const { t } = useI18n();

const hasInbox = computed(() => {
  if (store.state.onboarding.inbox?.id) return true;
  const inboxes = store.getters['inboxes/getInboxes'] || [];
  return inboxes.length > 0;
});
const choice = ref(hasInbox.value ? 'existing' : 'widget');

function selectCard(value) {
  if (value === 'existing' && !hasInbox.value) return;
  choice.value = value;
}

function continueSetup() {
  emit('select', choice.value);
}

useRegisterOnboardingNav(() => ({
  primary: {
    label: t('ONBOARDING_WIZARD.CONTINUE'),
    disabled: !choice.value,
    onClick: continueSetup,
  },
  onBack: () => emit('back'),
  backLabel: t('ONBOARDING_WIZARD.RETURN_TO_HUB'),
}));

function cardClass(active, disabled = false) {
  return [
    'w-full text-left rounded-2xl border p-5 transition-colors',
    disabled
      ? 'opacity-50 cursor-not-allowed border-slate-200 dark:border-slate-700'
      : active
        ? 'border-woot-500 bg-woot-50/50 dark:bg-woot-900/20'
        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600',
  ];
}
</script>

<template>
  <OnboardingStepLayout
    :title="t('ONBOARDING_WIZARD.AI_ENTRY.TITLE')"
    :description="t('ONBOARDING_WIZARD.AI_ENTRY.DESCRIPTION')"
    :step-label="t('ONBOARDING_WIZARD.OVERVIEW.PHASES.starting_with_ai.TITLE')"
  >
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
      <button
        type="button"
        :class="cardClass(choice === 'existing', !hasInbox)"
        :disabled="!hasInbox"
        @click="selectCard('existing')"
      >
        <Icon icon="i-lucide-inbox" class="size-8 text-woot-500 mb-3" />
        <p class="m-0 text-base font-semibold text-slate-900 dark:text-white">
          {{ t('ONBOARDING_WIZARD.AI_ENTRY.EXISTING') }}
        </p>
        <p class="mt-1 mb-0 text-sm text-slate-500 dark:text-slate-400">
          {{
            hasInbox
              ? t('ONBOARDING_WIZARD.AI_ENTRY.EXISTING_HELP')
              : t('ONBOARDING_WIZARD.AI_ENTRY.NO_INBOX')
          }}
        </p>
      </button>

      <button
        type="button"
        :class="cardClass(choice === 'widget')"
        @click="selectCard('widget')"
      >
        <Icon icon="i-lucide-message-square" class="size-8 text-woot-500 mb-3" />
        <p class="m-0 text-base font-semibold text-slate-900 dark:text-white">
          {{ t('ONBOARDING_WIZARD.AI_ENTRY.WIDGET') }}
        </p>
        <p class="mt-1 mb-0 text-sm text-slate-500 dark:text-slate-400">
          {{ t('ONBOARDING_WIZARD.AI_ENTRY.WIDGET_HELP') }}
        </p>
      </button>
    </div>
  </OnboardingStepLayout>
</template>
